import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  TextField,
  Autocomplete,
  Checkbox,
  ListItemText,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  Snackbar,
  Alert,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import useSWR from "swr";
import axiosInstance, { fetcher, postFetcher, patchFetcher } from "src/utils/axios";
import { useSnackbar } from "src/components/snackbar";

const CourseGroupDialog = ({
  currentID,
  open,
  onClose,
  deleteMode = false,
  onDeleteRow,
  onSaveSuccess,
  onDeleteSuccess,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [inputValue, setInputValue] = useState("");
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [newSelectedOption, setNewSelectedOption] = useState(null);
  const [revertStudents, setRevertStudents] = useState("No");
  const [deleteNewBatches, setDeleteNewBatches] = useState("No");
  const [suffixValue, setSuffixValue] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: groupData } = useSWR(
    currentID && !deleteMode ? `/courses/api/course-groups/${currentID}/` : null,
    fetcher
  );

  const { data: coursesData, error: coursesError, isLoading } = useSWR(
    "/courses/api/list/offline-courses/",
    fetcher
  );

  useEffect(() => {
    if (groupData && coursesData && !deleteMode) {
      setInputValue(groupData.name);
      setSuffixValue(groupData.suffix || "");
      setRevertStudents("No"); // Default for edit
      setDeleteNewBatches("No"); // Default for delete

      const selectedCourses = coursesData.offline_courses.filter((course) =>
        groupData.courses.includes(course.id)
      );
      setSelectedOptions(
        selectedCourses.map((course) => ({
          id: course.id,
          label: course.name,
        }))
      );

      const targetCourse = coursesData.offline_courses.find(
        (course) => course.id === groupData.target_course
      );
      setNewSelectedOption(
        targetCourse
          ? {
              id: targetCourse.id,
              label: targetCourse.name,
            }
          : null
      );
    } else if (!currentID && !open) {
      // Reset form on dialog close when creating
      setInputValue("");
      setSelectedOptions([]);
      setNewSelectedOption(null);
      setSuffixValue("");
      setRevertStudents("No");
      setDeleteNewBatches("No");
    }
  }, [groupData, coursesData, deleteMode, currentID, open]);

  const offlineCourses = coursesData?.offline_courses || [];

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const payload = {
        name: inputValue,
        suffix: suffixValue,
        courses: selectedOptions.map((option) => option.id),
        target_course: newSelectedOption?.id,
        keep_same_batches: true,
        continue_with_batches: false,
        keep_students_unallocated: false,
        ...(currentID && { revert_students: revertStudents === "Yes" }),
      };

      if (currentID) {
        await patchFetcher([
          `/courses/api/course-grouping/edit/new/${currentID}/`,
          payload,
        ]);
        enqueueSnackbar("Course group updated successfully.", { variant: "success" });
      } else {
        await postFetcher([
          `/courses/api/course-grouping/create/new/`,
          payload,
        ]);
        enqueueSnackbar("Course group created successfully.", { variant: "success" });
      }

      if (onSaveSuccess) {
        await onSaveSuccess();
      }
      onClose();
    } catch (err) {
      console.error("Error saving course group:", err);
      const errorMessage =
        typeof err === "string"
          ? err
          : err?.error || err?.message || "Failed to save course group";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await axiosInstance.delete(`/courses/api/course-grouping/delete/new/${currentID}/`, {
        data: {
          revert_students: revertStudents === "Yes",
          delete_new_batches: deleteNewBatches === "Yes",
        },
      });
      enqueueSnackbar("Course group deleted successfully.", { variant: "success" });
      if (onDeleteSuccess) {
        await onDeleteSuccess();
      } else if (onDeleteRow) {
        await onDeleteRow();
      }
      onClose();
    } catch (err) {
      console.error("Error deleting course group:", err);
      const errorMessage =
        typeof err === "string"
          ? err
          : err?.error || err?.message || "Failed to delete course group";
      enqueueSnackbar(errorMessage, { variant: "error" });
      setError(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleToggleOption = (option) => {
    setSelectedOptions((prev) => {
      if (prev.some((o) => o.id === option.id)) {
        return prev.filter((o) => o.id !== option.id);
      }
      return [...prev, option];
    });
  };

  const isSubmitDisabled =
    isSubmitting ||
    (!deleteMode &&
      (inputValue.trim() === "" ||
        selectedOptions.length === 0 ||
        !newSelectedOption?.id 
        // (keepSameBatches === "Yes" && suffixValue.trim() === "")
      ));

  const formattedOptions = offlineCourses.map((course) => ({
    label: `${course.name}`,
    id: course.id,
  }));

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(null);
  };

  if (isLoading) return <Typography>Loading...</Typography>;
  if (coursesError) return <Typography>Error loading courses</Typography>;

  return (
    <>
      <Dialog
        open={open}
        onClose={isSubmitting || isDeleting ? undefined : onClose}
        PaperProps={{
          sx: {
            minWidth: "400px",
            maxWidth: "600px",
            width: "90%",
          },
        }}
      >
        <DialogTitle>
          {deleteMode ? "Delete Course Group" : currentID ? "Edit Course Group" : "Create Course Group"}
        </DialogTitle>
        <DialogContent>
          {deleteMode ? (
            <>
              <Typography sx={{ mb: 2 }}>
                Are you sure you want to delete this course group?
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginTop: 2,
                }}
              >
                <Typography>Revert students to original batches?</Typography>
                <RadioGroup
                  row
                  value={revertStudents}
                  onChange={(e) => setRevertStudents(e.target.value)}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginTop: 2,
                }}
              >
                <Typography>Delete new batches?</Typography>
                <RadioGroup
                  row
                  value={deleteNewBatches}
                  onChange={(e) => setDeleteNewBatches(e.target.value)}
                >
                  <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                  <FormControlLabel value="No" control={<Radio />} label="No" />
                </RadioGroup>
              </Box>
            </>
          ) : (
            <>
              <TextField
                label="Course Group Name"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                fullWidth
                margin="dense"
                variant="outlined"
                error={inputValue.trim() === ""}
                helperText={inputValue.trim() === "" ? "Name is required" : ""}
              />
              <Autocomplete
                multiple
                options={formattedOptions}
                getOptionLabel={(option) => option.label}
                renderOption={(props, option) => {
                  const isChecked = selectedOptions.some((o) => o.id === option.id);
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={isChecked}
                        onClick={(event) => {
                          event.stopPropagation();
                          handleToggleOption(option);
                        }}
                      />
                      <ListItemText primary={option.label} />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Source Courses"
                    margin="dense"
                    variant="outlined"
                    error={selectedOptions.length === 0}
                    helperText={selectedOptions.length === 0 ? "Select at least one course" : ""}
                  />
                )}
                value={selectedOptions}
                onChange={(_, newValue) => setSelectedOptions(newValue)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                fullWidth
              />
              <Autocomplete
                options={formattedOptions}
                getOptionLabel={(option) => option?.label || ""}
                renderOption={(props, option) => {
                  const isChecked = newSelectedOption?.id === option.id;
                  return (
                    <li {...props}>
                      <Checkbox
                        checked={isChecked}
                        onClick={(event) => {
                          event.stopPropagation();
                          setNewSelectedOption(
                            newSelectedOption?.id === option.id ? null : option
                          );
                        }}
                      />
                      <ListItemText primary={option.label} />
                    </li>
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Target Course"
                    margin="dense"
                    variant="outlined"
                    error={!newSelectedOption?.id}
                    helperText={!newSelectedOption?.id ? "Select a target course" : ""}
                  />
                )}
                value={newSelectedOption}
                onChange={(_, newValue) => setNewSelectedOption(newValue)}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                fullWidth
              />
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  marginTop: 2,
                }}
              >
                <Alert severity="info" sx={{ width: "100%" }}>
                  Target-course batches and their students will remain allocated as they are.
                  Every student from every selected source course will be shown as unallocated
                  in the course group, regardless of their source batch.
                </Alert>
              </Box>
              {currentID && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 2,
                    marginTop: 2,
                  }}
                >
                  <Typography>Revert students of removed courses to original batches?</Typography>
                  <RadioGroup
                    row
                    value={revertStudents}
                    onChange={(e) => setRevertStudents(e.target.value)}
                  >
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="outlined" disabled={isSubmitting || isDeleting}>
            Cancel
          </Button>
          {deleteMode ? (
            <LoadingButton
              onClick={handleDelete}
              variant="contained"
              color="error"
              loading={isDeleting}
            >
              Delete
            </LoadingButton>
          ) : (
            <LoadingButton
              onClick={handleSubmit}
              variant="contained"
              loading={isSubmitting}
              disabled={isSubmitDisabled}
            >
              {currentID ? "Update" : "Create"}
            </LoadingButton>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: "100%" }}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

CourseGroupDialog.propTypes = {
  currentID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  deleteMode: PropTypes.bool,
  onClose: PropTypes.func,
  onDeleteRow: PropTypes.func,
  onDeleteSuccess: PropTypes.func,
  onSaveSuccess: PropTypes.func,
  open: PropTypes.bool,
};

export default CourseGroupDialog;
